import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UserPlus, Mail, Phone, Pencil, Trash2, Loader2, MessageCircle, KeyRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  given_name: "",
  last_name: "",
  relationship: "Parent",
  email: "",
  phone: "",
  notify_email: true,
  notify_whatsapp: true,
};

export const SecondParentCard = () => {
  const [contact, setContact] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [inviting, setInviting] = useState(false);

  const invite = async () => {
    setInviting(true);
    try {
      const res = await axios.post(`${API}/academy/me/second-parent/invite`);
      await load();
      toast.success(
        res.data?.emailed
          ? `Portal invite sent to ${res.data.email}`
          : "Invite created — email could not be sent, share the setup link"
      );
      if (!res.data?.emailed && res.data?.setup_link) {
        console.info("Second-parent setup link:", res.data.setup_link);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send the invite");
    } finally {
      setInviting(false);
    }
  };

  const revokeAccess = async () => {
    try {
      await axios.delete(`${API}/academy/me/second-parent/access`);
      await load();
      toast.success("Portal access revoked");
    } catch (err) {
      toast.error("Could not revoke access");
    }
  };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/academy/me/second-parent`);
      setContact(res.data?.second_parent || null);
    } catch (err) {
      setContact(null);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openEditor = () => {
    setForm(contact ? { ...EMPTY, ...contact } : EMPTY);
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/academy/me/second-parent`, form);
      setContact(res.data.second_parent);
      setOpen(false);
      toast.success("Second parent saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save second parent");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await axios.delete(`${API}/academy/me/second-parent`);
      setContact(null);
      toast.success("Second parent removed");
    } catch (err) {
      toast.error("Could not remove second parent");
    }
  };

  return (
    <Card className="border-sky-500/30 bg-sky-500/5" data-testid="second-parent-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sky-200 text-base md:text-lg">
          <UserPlus className="w-5 h-5" /> Second Parent / Guardian
        </CardTitle>
      </CardHeader>
      <CardContent>
        {contact ? (
          <div className="space-y-3" data-testid="second-parent-details">
            <div>
              <p className="text-white font-semibold">
                {contact.name || `${contact.given_name} ${contact.last_name}`}
                <span className="ml-2 text-xs text-slate-400">{contact.relationship}</span>
              </p>
              <div className="mt-1 space-y-1 text-sm text-slate-300">
                {contact.email && (
                  <p className="flex items-center gap-2" data-testid="second-parent-email">
                    <Mail className="w-3.5 h-3.5 text-slate-400" /> {contact.email}
                  </p>
                )}
                {contact.phone && (
                  <p className="flex items-center gap-2" data-testid="second-parent-phone">
                    <Phone className="w-3.5 h-3.5 text-slate-400" /> {contact.phone}
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge
                variant="outline"
                className={contact.notify_email ? "border-emerald-500/40 text-emerald-300" : "border-slate-600 text-slate-400"}
                data-testid="second-parent-email-optin"
              >
                <Mail className="w-3 h-3 mr-1" />
                {contact.notify_email ? "Gets invoice emails" : "No invoice emails"}
              </Badge>
              <Badge
                variant="outline"
                className={contact.notify_whatsapp ? "border-emerald-500/40 text-emerald-300" : "border-slate-600 text-slate-400"}
                data-testid="second-parent-whatsapp-optin"
              >
                <MessageCircle className="w-3 h-3 mr-1" />
                {contact.notify_whatsapp ? "Ready for WhatsApp" : "No WhatsApp"}
              </Badge>
              {contact.portal_access && (
                <Badge
                  variant="outline"
                  className="border-sky-500/40 text-sky-200"
                  data-testid="second-parent-portal-badge"
                >
                  <KeyRound className="w-3 h-3 mr-1" />
                  {contact.portal_password_set ? "Portal login active" : "Portal invite sent"}
                </Badge>
              )}
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={openEditor} data-testid="edit-second-parent-btn">
                <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
              </Button>
              {contact.email && !contact.portal_access && (
                <Button
                  size="sm"
                  className="bg-sky-600 hover:bg-sky-500"
                  onClick={invite}
                  disabled={inviting}
                  data-testid="invite-second-parent-btn"
                >
                  {inviting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <KeyRound className="w-3.5 h-3.5 mr-1" />}
                  Give portal access
                </Button>
              )}
              {contact.portal_access && (
                <>
                  <Button size="sm" variant="outline" onClick={invite} disabled={inviting} data-testid="resend-second-parent-invite-btn">
                    {inviting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <KeyRound className="w-3.5 h-3.5 mr-1" />}
                    Resend setup link
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-amber-300 hover:text-amber-200"
                    onClick={revokeAccess}
                    data-testid="revoke-second-parent-access-btn"
                  >
                    Revoke access
                  </Button>
                </>
              )}
              <Button
                size="sm"
                variant="ghost"
                className="text-red-300 hover:text-red-200"
                onClick={remove}
                data-testid="remove-second-parent-btn"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-300">
              Add a second parent or guardian so they receive invoice emails and academy WhatsApp updates too.
              You can also give them their own read-only portal login afterwards.
            </p>
            <Button
              size="sm"
              className="bg-sky-600 hover:bg-sky-500"
              onClick={openEditor}
              data-testid="add-second-parent-btn"
            >
              <UserPlus className="w-4 h-4 mr-1" /> Add second parent
            </Button>
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-700" data-testid="second-parent-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">
              {contact ? "Edit second parent" : "Add second parent"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300 text-xs">Given name *</Label>
                <Input
                  value={form.given_name}
                  onChange={(e) => setForm({ ...form, given_name: e.target.value })}
                  data-testid="second-parent-given-name"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Last name *</Label>
                <Input
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  data-testid="second-parent-last-name"
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-300 text-xs">Relationship</Label>
              <Input
                value={form.relationship}
                onChange={(e) => setForm({ ...form, relationship: e.target.value })}
                placeholder="Mother / Father / Guardian"
                data-testid="second-parent-relationship"
              />
            </div>
            <div>
              <Label className="text-slate-300 text-xs">Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="second-parent-email-input"
              />
            </div>
            <div>
              <Label className="text-slate-300 text-xs">Mobile (for WhatsApp)</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+65 9123 4567"
                data-testid="second-parent-phone-input"
              />
            </div>
            <div className="flex items-center justify-between rounded-lg bg-slate-800/60 px-3 py-2">
              <div>
                <p className="text-sm text-white">Email notifications</p>
                <p className="text-xs text-slate-400">Copy them on invoices and payment emails</p>
              </div>
              <Switch
                checked={!!form.notify_email}
                onCheckedChange={(v) => setForm({ ...form, notify_email: v })}
                data-testid="second-parent-notify-email-switch"
              />
            </div>
            <div className="flex items-center justify-between rounded-lg bg-slate-800/60 px-3 py-2">
              <div>
                <p className="text-sm text-white">WhatsApp messages</p>
                <p className="text-xs text-slate-400">Include them once WhatsApp updates go live</p>
              </div>
              <Switch
                checked={!!form.notify_whatsapp}
                onCheckedChange={(v) => setForm({ ...form, notify_whatsapp: v })}
                data-testid="second-parent-notify-whatsapp-switch"
              />
            </div>
            <Button
              onClick={save}
              disabled={saving}
              className="w-full bg-sky-600 hover:bg-sky-500"
              data-testid="save-second-parent-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save second parent
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default SecondParentCard;
